import { Product } from "@/sdk/types";
import { cloneNode } from "@/ui-sdk/ui-script/utils";

export const renderCloneNode = async (item: Product) => {
  const templateNode = window.helium.datastore.memory.html.product;
  const handle =
    window?.helium?.productCardHandle ||
    window.helium.datastore.memory.html.productHandle ||
    window.location.pathname.split("/").at(-1);

  if (!templateNode) return null;

  const existingProduct = (
    await window.helium.datastore.getProduct({
      id: handle ?? "",
    })
  ).details;

  const node = cloneNode(templateNode, item, existingProduct, false, {
    componentType: "memory_recommendations",
    eventData: {},
  });

  node.className += " grid__item  md:hp-border";
  return { __html: node.outerHTML };
};

export const getText = async ({
  query,
  brandInfo,
  typingTextElementRef,
  backendUrl,
}: {
  query: string;
  brandInfo: string;
  typingTextElementRef: React.MutableRefObject<HTMLElement | null>;
  backendUrl: string;
}): Promise<void> => {
  const textElement = typingTextElementRef.current;
  if (!textElement) {
    console.error("Typing text element not found during getText.");
    return Promise.resolve();
  }

  textElement.innerHTML = "";
  textElement.style.display = "block";

  try {
    const response = await fetch(`${backendUrl}/api/chat/text`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        brandId: window.helium.brandData.storeId,
      },
      body: JSON.stringify({
        query: query,
        brandInfo,
      }),
    });

    if (!response.ok) {
      console.error("Error fetching typing text:", response.statusText);
      textElement.textContent = "Could not generate a response.";
      return Promise.resolve();
    }

    const msg = await response.text();

    return new Promise<void>((resolve) => {
      let index = 0;
      const typingSpeed = 30;
      const postTypingDelay = 500;

      function typeText() {
        if (!document.body.contains(textElement)) {
          console.warn("Typing text element removed from DOM during typing.");
          resolve();
          return;
        }

        if (index < msg.length) {
          textElement.innerHTML += msg.charAt(index);
          index++;
          setTimeout(typeText, typingSpeed);
        } else {
          setTimeout(() => {
            resolve();
          }, postTypingDelay);
        }
      }

      typeText();
      requestAnimationFrame(() => {
        if (textElement && document.body.contains(textElement)) {
          textElement.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    });
  } catch (error) {
    console.error("Exception during getText:", error);
    if (textElement) {
      textElement.textContent = "An error occurred while generating text.";
    }
    return Promise.resolve();
  }
};

type ProcessSearchParams = {
  searchValue: string;
  productArea: HTMLElement | null;
  pagination?: HTMLElement | null;
  mainProductHeading: HTMLElement | null;
  setMainProductHeading: (el: HTMLElement) => void;
  setLoading: (val: boolean) => void;
  getDescription: (headingEl: HTMLElement) => Promise<void>;
  typingTextElementRef: React.MutableRefObject<HTMLElement | null>;
};

export const processSearch = async ({
  searchValue,
  productArea,
  pagination,
  mainProductHeading,
  setMainProductHeading,
  setLoading,
  getDescription,
  typingTextElementRef,
}: ProcessSearchParams) => {
  if (!searchValue.trim()) return;
  if (!productArea) {
    console.error("Product area DOM element not found.");
    return;
  }

  setLoading(true);
  pagination?.style.setProperty("display", "none", "important");

  productArea.innerHTML = "";

  let typingTextElement = document.getElementById("typing-text") as HTMLElement;
  if (!typingTextElement) {
    typingTextElement = document.createElement("h2");
    typingTextElement.id = "typing-text";
    typingTextElement.className = "hp-text-gray-700 hp-text-left";
    typingTextElement.style.cssText =
      "font-size: 14px; font-weight: normal; display:block";

    const card = document.createElement("div");
    card.className =
      "hp-mx-auto hp-flex hp-items-center hp-gap-4 hp-rounded-full hp-bg-slate-50 hp-px-6 hp-py-4 hp-shadow-sm hp-transition-colors hover:hp-bg-slate-100";

    const img = document.createElement("img");
    img.src = window?.heliumComponenConfigs?.logo || "";
    img.alt = "icon";
    img.className = "hp-h-8 hp-w-9";

    card.appendChild(img);
    card.appendChild(typingTextElement);
    productArea.appendChild(card);
  }

  typingTextElement.style.display = "block";
  typingTextElement.textContent = "We are curating for you...";
  typingTextElementRef.current = typingTextElement;

  let currentMainHeading = mainProductHeading;
  if (!currentMainHeading) {
    currentMainHeading = document.createElement("h2");
    currentMainHeading.id = "product-heading";
    currentMainHeading.className = "hp-text-black hp-capitalize hp-mt-4";
    currentMainHeading.style.cssText =
      "font-size: 20px; font-weight: bold; display: block;";

    if (productArea.parentNode) {
      productArea.parentNode.insertBefore(currentMainHeading, productArea);
    } else {
      productArea.prepend(currentMainHeading);
    }

    setMainProductHeading(currentMainHeading);
  }

  currentMainHeading.style.display = "block";
  currentMainHeading.textContent = "";

  await getDescription(currentMainHeading);
};

export const initializeDOMRefs = (
  setProductArea: (el: HTMLElement | null) => void,
  setPagination: (el: HTMLElement | null) => void,
  setMainProductHeading: (el: HTMLElement | null) => void,
) => {
  requestAnimationFrame(() => {
    const area =
      document.querySelector("#product-grid") ??
      document.querySelector(
        ".grid.grid-cols-2.sm\\:grid-cols-3.lg\\:grid-cols-4.gap-4",
      );
    setProductArea(area as HTMLElement | null);

    const pagi = document.querySelector(".pagination-wrapper") as HTMLElement;
    setPagination(pagi || null);

    const heading = document.querySelector("#product-heading") as HTMLElement;
    setMainProductHeading(heading || null);
  });
};

export const handleKeyboardEscape = (callback: () => void) => {
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") callback();
  };
  document.addEventListener("keydown", onKeyDown);
  return () => document.removeEventListener("keydown", onKeyDown);
};
